import { Award, Brain, Flame, Heart, Star, TrendingUp, Zap } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { API_URL } from '../../config/api';

interface Milestone {
  id: number;
  memory_type: string;
  content: string;
  importance: number;
  created_at: string;
}

interface MertebeHistory {
  id: number;
  old_mertebe: string;
  new_mertebe: string;
  trigger_reason: string;
  created_at: string;
}

interface Props {
  agentId: number;
}

const MILESTONE_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  evolution_milestone: { icon: <TrendingUp size={12} />, color: '#a78bfa', label: 'Evolution' },
  anne_feedback:       { icon: <Heart size={12} />,      color: '#f472b6', label: 'Mentor Feedback' },
  graduation:          { icon: <Award size={12} />,      color: '#fbbf24', label: 'Graduation' },
  anne_roaming_report: { icon: <Star size={12} />,       color: '#34d399', label: 'Evaluation' },
};

const MERTEBE_COLORS: Record<string, string> = {
  NASUT: 'var(--secondary)', MELEKUT: '#3b82f6', CEBERUT: '#8b5cf6', LAHUT: '#f59e0b', HAHUT: '#ffffff',
};
const MERTEBE_LABELS: Record<string, string> = {
  NASUT: 'Tier 1', MELEKUT: 'Tier 2', CEBERUT: 'Tier 3', LAHUT: 'Tier 4', HAHUT: 'Tier 5',
};

type ViewMode = 'timeline' | 'mertebe';

export default function GrowthJourney({ agentId }: Props) {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [mertebeHistory, setMertebeHistory] = useState<MertebeHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<ViewMode>('timeline');

  useEffect(() => {
    if (!agentId) return;
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/api/agents/${agentId}/memories?type=evolution_milestone,anne_feedback,graduation,anne_roaming_report&limit=20`)
        .then(r => r.json()).then(d => setMilestones(Array.isArray(d) ? d : [])).catch(() => setMilestones([])),
      fetch(`${API_URL}/api/esma/profile/${agentId}/mertebe-history`)
        .then(r => r.json()).then(d => setMertebeHistory(d?.history || [])).catch(() => setMertebeHistory([])),
    ]).finally(() => setLoading(false));
  }, [agentId]);

  if (loading) return <div style={{ padding: 16, textAlign: 'center', color: 'var(--secondary)', fontSize: 13 }}>Loading journey...</div>;

  if (milestones.length === 0 && mertebeHistory.length === 0) return (
    <div style={{ padding: 16, textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>🌱</div>
      <div style={{ fontSize: 13, color: 'var(--secondary)' }}>No milestones yet — keep growing!</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          <TrendingUp size={14} color="#a78bfa" /> Growth Journey
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['timeline', 'mertebe'] as ViewMode[]).map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              padding: '3px 10px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer',
              background: view === v ? '#a78bfa' : 'var(--body-bg)', color: view === v ? '#fff' : 'var(--text-secondary)',
            }}>
              {v === 'timeline' ? '📅 Timeline' : '⬆️ Tier'}
            </button>
          ))}
        </div>
      </div>

      {view === 'timeline' && (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', left: 11, top: 0, bottom: 0, width: 1, background: 'var(--border-color)' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 28 }}>
            {milestones.map((m) => {
              const cfg = MILESTONE_CONFIG[m.memory_type] || { icon: <Brain size={12} />, color: 'var(--secondary)', label: m.memory_type };
              return (
                <div key={m.id} style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: -22, top: 6, width: 16, height: 16, borderRadius: '50%', background: 'var(--card-bg)', border: `2px solid ${cfg.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: cfg.color }}>
                    {cfg.icon}
                  </div>
                  <div style={{ background: 'var(--body-bg)', borderRadius: 10, padding: '10px 12px', border: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: cfg.color, textTransform: 'uppercase' }}>{cfg.label}</span>
                      {m.importance >= 9 && <span style={{ fontSize: 10, color: '#fbbf24' }}><Flame size={10} /> High Impact</span>}
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.5, margin: 0 }}>
                      {m.content.length > 120 ? m.content.substring(0, 120) + '...' : m.content}
                    </p>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                      {new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === 'mertebe' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {mertebeHistory.length === 0
            ? <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 12 }}>No tier changes yet</div>
            : mertebeHistory.map((h) => {
              const fromColor = MERTEBE_COLORS[h.old_mertebe] || 'var(--secondary)';
              const toColor = MERTEBE_COLORS[h.new_mertebe] || 'var(--secondary)';
              return (
                <div key={h.id} style={{ background: 'var(--body-bg)', borderRadius: 10, padding: 12, border: `1px solid ${toColor}30` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: fromColor, background: `${fromColor}20`, padding: '2px 8px', borderRadius: 6 }}>{MERTEBE_LABELS[h.old_mertebe] || h.old_mertebe}</span>
                    <Zap size={12} color="#9ca3af" />
                    <span style={{ fontSize: 11, fontWeight: 700, color: toColor, background: `${toColor}20`, padding: '2px 8px', borderRadius: 6 }}>{MERTEBE_LABELS[h.new_mertebe] || h.new_mertebe}</span>
                  </div>
                  {h.trigger_reason && <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: 0 }}>{h.trigger_reason.substring(0, 80)}</p>}
                  <span style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                    {new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              );
            })
          }
        </div>
      )}
    </div>
  );
}
